import React, { useContext, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import Summary from "./Summary";
import { COLORS } from "../../assets/colors";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { GlobalContext } from "../../contexts/globalContext";
import CircularLoader from "../../components/UI/Loader";
import config from "../../config";

function Payment() {
  const [currentRide, setCurrentRide] = useState({});
  const { isAuthenticated, currentUser } = useSelector((state) => state.auth);
  const { rideId } = useParams();
  const [loading, setLoading] = useState(false);
  const { searchFields } = useContext(GlobalContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (rideId) {
      getRideById(rideId);
    }
  }, [rideId]);

  async function getRideById(rideId) {
    setLoading(true);
    const response = await api.get(`ride/${rideId}`);
    setCurrentRide(response.data.data);
    setLoading(false);
  }

  const submitPayment = async (totalAmountToPay, seatDetails = {}) => {
    try {
      if (!isAuthenticated) return;
      if (currentRide?.female_only && currentUser.gender != "female") {
        toast.error("This is Female Only ride");
        return;
      }
      setLoading(true);
      const response = await api.post("/payment", {
        ride_id: currentRide?.id,
        paid_by: currentUser?.id,
        paid_to: currentRide?.User.id,
        amount: totalAmountToPay,
        seats: seatDetails.seats || parseInt(searchFields.seats || 1),
        front_seats: seatDetails.front_seats || 0,
        back_seats: seatDetails.back_seats || 0,
        seat_type: seatDetails.seat_type || "custom",
      });

      if (response.data.data) {
        setLoading(false);
        window.location.href = response.data.data;
      }
    } catch (e) {
      setLoading(false);
      toast.error("Something went wrong");
    }
  };
  const sendFreeRequest = async (totalAmountToPay, seatDetails = {}) => {
    try {
      if (!isAuthenticated) return;
      if (currentRide?.female_only && currentUser.gender != "female") {
        toast.error("This is Female Only ride");
        return;
      }
      setLoading(true);
      const response = await api.post("/payment/send-free-request", {
        ride_id: currentRide?.id,
        paid_by: currentUser?.id,
        paid_to: currentRide?.User.id,
        amount: totalAmountToPay,
        seats: seatDetails.seats || parseInt(searchFields.seats || 1),
        front_seats: seatDetails.front_seats || 0,
        back_seats: seatDetails.back_seats || 0,
        seat_type: seatDetails.seat_type || "custom",
      });

      if (response.status == 200) {
        setLoading(false);
        navigate(
          `/active-ride/${currentRide.id}/payment/confirm/transactionId=free`
        );
      }
    } catch (e) {
      setLoading(false);
      toast.error("Something went wrong");
    }
  };
  return (
    <PageContainer>
      <HeroBanner>
        <div className="banner-content">
          <span className="badge">
            {config.featureFlags.freeLaunch ? "🎉 Free Launch" : "💳 Secure Payment"}
          </span>
          <h1>
            {config.featureFlags.freeLaunch
              ? "Payment Info"
              : "Pay Service Charges"}
          </h1>
          <p className="subtitle">
            Review your booking details and confirm your ride
          </p>
        </div>
        <div className="banner-decoration" />
      </HeroBanner>

      <ContentContainer>
        <div className="summary-wrapper">
          <Summary
            loading={loading}
            ride={currentRide}
            submitPayment={submitPayment}
            sendFreeRequest={sendFreeRequest}
          />
        </div>
      </ContentContainer>
    </PageContainer>
  );
}

export default Payment;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 4rem;
`;

const HeroBanner = styled.div`
  position: relative;
  background: linear-gradient(135deg, #054752 0%, #006C6D 50%, #1B937A 100%);
  padding: 3rem 2rem 4rem;
  overflow: hidden;
  text-align: center;

  .banner-content {
    position: relative;
    z-index: 2;
    max-width: 600px;
    margin: 0 auto;
    animation: ${fadeInUp} 0.6s ease-out;
  }

  .badge {
    display: inline-block;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.4rem 1.2rem;
    border-radius: 100px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: 0.5px;
    margin-bottom: 1rem;
    text-transform: uppercase;
  }

  h1 {
    font-size: 2rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 0.5rem;
    letter-spacing: -0.5px;

    @media (max-width: 600px) {
      font-size: 1.6rem;
    }
  }

  .subtitle {
    color: rgba(255, 255, 255, 0.75);
    font-size: 1rem;
    font-weight: 400;
    margin: 0;
  }

  .banner-decoration {
    position: absolute;
    top: -50%;
    right: -10%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.04);
    pointer-events: none;
  }

  &::before {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 40px;
    background: #f0f4f8;
    border-radius: 40px 40px 0 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 640px;
  margin: -1.5rem auto 0;
  padding: 0 1rem;
  position: relative;
  z-index: 3;
  animation: ${fadeInUp} 0.7s ease-out 0.1s both;

  .summary-wrapper {
    width: 100%;
  }

  @media (max-width: 546px) {
    padding-bottom: 2rem;
  }
`;
